import { Star } from "lucide-react";
import { toast } from "@/components/ui/use-toast";
import { extractErrorMessage } from "@/api/client";
import {
  useNastavPreferovanehoDodavatela,
  usePreferovaniDodavatelia,
  type ZnackaPrehlad,
} from "@/api/hooks/useCennik";

const BEZ = "";

function pocet(n: number): string {
  return n.toLocaleString("sk-SK");
}

/**
 * Preferovaný dodávateľ značky (KOLO 166).
 *
 * Ten istý výrobok Blumu predáva TWD, Demos aj Nábytkár. Výbery (slot,
 * kovanie, Korpus Builder, import) ponúknu len riadok preferovaného
 * dodávateľa, kým ho má; ostatné sú v Cenníku so štítkom „aj u: …"
 * a vo výberoch za prepínačom „Aj iní dodávatelia". Uložené zákazky,
 * sety a obľúbené sa nikdy nepresmerujú — mení sa len ďalší výber.
 *
 * Pri každej voľbe je počet, koľko riadkov by sa skrylo, takže dopad
 * je vidieť skôr, než sa niečo zmení.
 */
export function PreferovaniDodavateliaSection(): JSX.Element {
  const query = usePreferovaniDodavatelia();
  const nastav = useNastavPreferovanehoDodavatela();
  const znacky = query.data?.data ?? [];

  async function zmen(z: ZnackaPrehlad, dodavatel: string): Promise<void> {
    try {
      await nastav.mutateAsync({ znacka: z.znacka, dodavatel: dodavatel || null });
      toast({
        title: dodavatel
          ? `${z.znacka}: výbery ponúkajú ${
              z.dodavatelia.find((d) => d.dodavatel === dodavatel)?.nazov ?? dodavatel
            }`
          : `${z.znacka}: bez preferencie — výbery ponúkajú všetkých`,
      });
    } catch (e) {
      toast({
        variant: "destructive",
        title: "Neuložené",
        description: extractErrorMessage(e),
      });
    }
  }

  return (
    <section className="space-y-3 rounded-lg border bg-card p-4">
      <div>
        <h2 className="flex items-center gap-2 text-xl">
          <Star className="h-5 w-5 text-primary" />
          Preferovaní dodávatelia
        </h2>
        <p className="text-xs text-muted-foreground">
          Ten istý výrobok (rovnaký kód výrobcu) predáva viac dodávateľov.
          Výbery ponúknu len preferovaného, kým ho má; ostatní sú v Cenníku
          so štítkom „aj u: …“ a vo výberoch za prepínačom „Aj iní
          dodávatelia“. Uložené zákazky, sety ani obľúbené sa nemenia.
        </p>
      </div>

      {query.isLoading ? (
        <p className="text-sm text-muted-foreground">Načítavam…</p>
      ) : znacky.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          V cenníku nie je značka s kódom výrobcu — kódy dopĺňa import cenníka.
        </p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-1.5 pr-3 font-medium">Značka</th>
              <th className="py-1.5 pr-3 font-medium">Dodávatelia (riadky s kódom)</th>
              <th className="py-1.5 pr-3 text-right font-medium">Spoločných výrobkov</th>
              <th className="py-1.5 pr-3 font-medium">Preferovaný</th>
              <th className="py-1.5 text-right font-medium">Skrytých vo výberoch</th>
            </tr>
          </thead>
          <tbody>
            {znacky.map((z) => (
              <tr key={z.znacka} className="border-b last:border-0">
                <td className="py-2 pr-3 font-medium">{z.znacka}</td>
                <td className="py-2 pr-3 text-xs">
                  {z.dodavatelia
                    .map((d) => `${d.nazov} ${pocet(d.pocet_s_kodom)}`)
                    .join(" · ")}
                </td>
                <td className="py-2 pr-3 text-right tabular-nums">
                  {pocet(z.rodin_u_viacerych)}
                </td>
                <td className="py-2 pr-3">
                  <select
                    aria-label={`Preferovaný dodávateľ ${z.znacka}`}
                    className="h-8 rounded-md border border-input bg-background px-2 text-xs"
                    value={z.preferovany ?? BEZ}
                    disabled={nastav.isPending}
                    onChange={(e) => void zmen(z, e.target.value)}
                  >
                    <option value={BEZ}>— bez preferencie —</option>
                    {z.dodavatelia.map((d) => (
                      <option key={d.dodavatel} value={d.dodavatel}>
                        {d.nazov}
                        {d.skryje > 0 ? ` (skryje ${pocet(d.skryje)})` : ""}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="py-2 text-right tabular-nums">
                  {pocet(z.skrytych_vo_vyberoch)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
